import React from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandLogo from "@components/BrandLogo";
import { parseCMSBold, getCouponAvailabilityLabel, isCouponSoldOut } from "@app/Helpers.js";
import LockIcon from "./img/lock-icon.svg?react";
import ArrowTip from "./img/arrow-tip-button.svg?react";
import CheckedIcon from "./img/checked-icon.svg?react";
import "./style.scss";
import { useSessionStorage } from "../../Helpers";
import { useQueryClient } from "@tanstack/react-query";
import {
  BLURRED_COUPON_IMAGE,
  isBlurredCouponIdent,
  levelMapper,
} from "../../Constants";
import DOMPurify from "dompurify";

function CostStatus({ points, availableForStatus, level }) {
  if (!availableForStatus) {
    return (
      <p className="cost">
        STATUS: <span> {levelMapper.get(level)}</span>
      </p>
    );
  }

  return (
    <p className="cost">
      Kostet: <span>{points} PUNKTE</span>
    </p>
  );
}

function calculateProgresPrecentage(value, maxValue) {
  return value / maxValue;
}

function ProgressBar({ value, maxValue }) {
  if (value >= maxValue) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <circle
          cx="10"
          cy="10"
          r="8"
          fill="white"
          stroke="#3D65AB"
          strokeWidth="2"
        />
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      style={{ transform: "rotate(-90deg)" }}
    >
      <circle
        cx="10"
        cy="10"
        r="8"
        fill="white"
        stroke="$white"
        strokeWidth="2"
      />
      <circle
        cx="10"
        cy="10"
        r="8"
        className="indicator"
        style={{
          // PI * 2 * radius
          strokeDasharray: `${Math.PI * 2 * 8}px`,
          strokeDashoffset: `${
            Math.PI * 2 * 8 * (1 - calculateProgresPrecentage(value, maxValue))
          }px`,
          stroke: "#3D65AB",
          strokeWidth: 2.5,
          strokeLinecap: "round",
        }}
      />
    </svg>
  );
}

function ProgressBarStatus({
  points,
  balance,
  availableForStatus,
  inStock,
  partner,
  partnerAvailable,
}) {
  if (!inStock) {
    return (
      <div className="progress-wrapper">
        <ProgressBar maxValue={1} value={1} />
        <LockIcon className="icon" />
      </div>
    );
  }

  if (partner && !partnerAvailable) {
    return (
      <div className="progress-wrapper">
        <ProgressBar maxValue={1} value={1} />
        <LockIcon className="icon" />
      </div>
    );
  }

  if (!availableForStatus) {
    return (
      <div className="progress-wrapper">
        <ProgressBar maxValue={1} value={1} />
        <LockIcon className="icon" />
      </div>
    );
  }

  if (balance >= points) {
    return (
      <div className="progress-wrapper">
        <ProgressBar maxValue={1} value={1} />
        <CheckedIcon className="icon" />
      </div>
    );
  }

  return (
    <div className="progress-wrapper">
      <ProgressBar maxValue={points} value={balance} />
    </div>
  );
}

function AwardStatus({
  availableForStatus,
  value,
  balance,
  inStock,
  partner,
  partnerAvailable,
}) {
  if (partner && !partnerAvailable) {
    return <p className="status">Gesperrt</p>;
  }

  if (!inStock) {
    return <p className="status">NICHT LIEFERBAR</p>;
  }

  if (!availableForStatus) {
    return <p className="status">Status unpassend</p>;
  }

  if (value > balance) {
    return (
      <p className="status">
        FEHLEND: <span className="cost">{value - balance} Punkte</span>
      </p>
    );
  }

  return <p className="status">Griffbereit</p>;
}

export default function AwardCard({
  mobileImg,
  desktopImg,
  imgDesc,
  copy,
  points,
  brands,
  disabled = false,
  ident,
  highlighted,
  inStock,
  href,
  className = "",
  availableForStatus,
  level,
  hasPoints,
  hasLevel,
  partner,
  partnerAvailable,
  onClick,
  prize,
  availabilityLevel = null,
}) {
  const [_, setAwardMissingPoints] = useSessionStorage(
    "awardMissingPoints",
    ""
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const data = queryClient.getQueryData(["user-details"]) ?? {};
  const isBlurred = isBlurredCouponIdent(ident);
  const cardMobileImg = isBlurred ? BLURRED_COUPON_IMAGE : mobileImg;
  const cardDesktopImg = isBlurred ? BLURRED_COUPON_IMAGE : desktopImg;
  const soldOut = isCouponSoldOut(availabilityLevel);
  const availabilityLabel = getCouponAvailabilityLabel(availabilityLevel);

  const determineLink = () => {
    if (ident === "static") return;
    if (prize) return `/prize/redeem/${prize}`;
    return `/coupons/redeem/${ident}`;
  };

  return (
    <div
      className={`award-card-component ${disabled || soldOut ? "disabled" : ""} ${
        className ? className : ""
      } ${highlighted ? "highlighted" : ""} ${isBlurred ? "blurred-ident" : ""}`}
    >
      <Link
        onClick={(e) => {
          if (
            determineLink() === "/activities" &&
            inStock &&
            availabilityLevel !== null
          ) {
            setAwardMissingPoints({
              ident,
              initialBalance: data.consumer.balance,
              awardPrice: points,
              awardName: copy,
            });
          }

          if (onClick) {
            onClick(e);
          }
        }}
        to={determineLink()}
      >
        <div className="inner">
          <div className="img-wrapper">
            {partner ? (
              !partnerAvailable ? (
                <div className="activities-link">
                  <span>
                    Erfahre, wie man den
                    <br /> Gutschein freischaltet
                  </span>
                  <ArrowTip />
                </div>
              ) : (
                (!hasLevel || !hasPoints) &&
                inStock &&
                !soldOut && (
                  <div className="activities-link">
                    <p
                      className="inner-link"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate("/activities");
                      }}
                    >
                      Mehr Punkte sammeln
                    </p>{" "}
                    <ArrowTip />
                  </div>
                )
              )
            ) : (
              (!hasLevel || !hasPoints) &&
              inStock &&
              !soldOut && (
                <div className="activities-link">
                  <p
                    className="inner-link"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate("/activities");
                    }}
                  >
                    Mehr Punkte sammeln
                  </p>{" "}
                  <ArrowTip />
                </div>
              )
            )}
            <picture>
              <source media="(min-width: 1020px)" srcSet={cardDesktopImg} />
              <img src={cardMobileImg} alt={isBlurred ? "" : imgDesc} />
            </picture>
          </div>
          <BrandLogo brands={brands} />

          <div className="award-info">
            <p
              className="copy"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parseCMSBold(copy))}}
            />
            <hr />
            {availabilityLabel && (
              <p className="availability">
                Verfügbarkeit: <span>{availabilityLabel}</span>
              </p>
            )}
            <div className="award-status">
              <CostStatus
                points={points}
                availableForStatus={availableForStatus}
                level={level}
              />
              <ProgressBarStatus
                partner={partner}
                partnerAvailable={partnerAvailable}
                inStock={inStock}
                balance={data?.consumer?.balance ?? 0}
                points={points}
                availableForStatus={availableForStatus}
              />
              <AwardStatus
                partner={partner}
                partnerAvailable={partnerAvailable}
                inStock={inStock}
                balance={data?.consumer?.balance ?? 0}
                value={points}
                availableForStatus={availableForStatus}
              />
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
